import React, { memo } from "react";
import { thumb400 } from "../../lib/imageVariants";

/**
 * Memoized game card used on the Home grid. Only re-renders when the game
 * object identity changes — critical for keeping a 60 FPS scroll on the
 * catalog when other pieces of the app (cart total, exchange rate, tour
 * step, etc.) trigger parent re-renders.
 */
export const GameCard = memo(function GameCard({ game, onSelect, onInactive }) {
  const isInactive = game.is_active === false;
  const handleClick = () => (isInactive ? onInactive(game) : onSelect(game));
  // Serve the 400 px WebP variant when the source lives in AG7 storage.
  // For admin-pasted external URLs (Cloudinary etc.) this is a no-op.
  const cardImg = thumb400(game.image);

  return (
    <div
      data-testid={`game-card-${game.slug}`}
      onClick={handleClick}
      aria-disabled={isInactive}
      className={`group relative rounded-2xl bg-ag7-surface border overflow-hidden transition-[border-color,transform,box-shadow] duration-300 flex flex-col shadow-lg sm:shadow-xl ${
        isInactive
          ? "border-red-900/40 cursor-not-allowed opacity-60"
          : "border-ag7-purple/25 hover:border-ag7-purple/80 cursor-pointer hover:-translate-y-1.5 hover:shadow-[0_22px_50px_-16px_rgba(0,0,0,0.95),0_0_32px_rgba(147,51,234,0.35)] active:scale-[0.98]"
      }`}
    >
      {isInactive && (
        <div
          data-testid={`game-inactive-badge-${game.slug}`}
          className="absolute top-1.5 left-1.5 right-1.5 sm:top-2 sm:left-2 sm:right-2 flex justify-center z-20"
        >
          <span className="px-2 py-0.5 rounded-full bg-red-950/90 border border-red-500/60 text-red-300 text-[9px] font-black uppercase tracking-widest shadow-md">
            🔧 No Disponible
          </span>
        </div>
      )}

      {/* Poster artwork — full-bleed cover with title overlaid on the image */}
      <div className={`relative w-full h-24 sm:h-44 overflow-hidden ${isInactive ? "grayscale" : ""}`}>
        {game.image ? (
          <img
            src={cardImg}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          // Rendered from a cached (image-less) copy while the fresh fetch is
          // in flight. Keeps the layout identical so images can pop in without
          // any reflow.
          <div aria-hidden="true" className="w-full h-full bg-zinc-800/70 animate-pulse" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-ag7-purple/30 to-transparent transition-opacity duration-300" aria-hidden="true" />
        {/* Title + category over the artwork, poster style */}
        <div className="absolute bottom-0 inset-x-0 p-1.5 sm:p-3 text-left">
          <h3 className="font-heading font-black text-[10px] sm:text-lg text-white leading-tight line-clamp-1 uppercase tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.95)]">{game.name}</h3>
          <p className="text-[8px] sm:text-[11px] text-purple-300 font-mono line-clamp-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
            💎 {game.category}
          </p>
        </div>
      </div>

      <div className="p-1.5 sm:p-3">
        <button data-testid="game-card-button-1"
          disabled={isInactive}
          className={`w-full font-bold py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl text-[9px] sm:text-xs shadow-md ${
            isInactive
              ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
              : "bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 hover:shadow-[0_0_18px_rgba(236,72,153,0.45)] transition-[opacity,box-shadow] duration-200 text-white"
          }`}
        >
          {isInactive ? "MANTENIMIENTO" : "VER PAQUETES →"}
        </button>
      </div>
    </div>
  );
});

/**
 * Lightweight skeleton shown for the initial catalog load when we have no
 * cached games. Keeps the same footprint as GameCard so the grid doesn't
 * jump when the real data lands.
 */
export const GameCardSkeleton = memo(function GameCardSkeleton() {
  return (
    <div
      data-testid="game-card-skeleton"
      aria-hidden="true"
      className="rounded-2xl bg-ag7-surface border border-ag7-purple/15 overflow-hidden flex flex-col shadow-lg sm:shadow-xl animate-pulse"
    >
      <div className="w-full h-24 sm:h-44 bg-zinc-800/80" />
      <div className="p-1.5 sm:p-3 w-full">
        <div className="h-7 sm:h-9 w-full rounded-lg sm:rounded-xl bg-zinc-800/60" />
      </div>
    </div>
  );
});
